import { Form, Formik, Field } from "formik";
import { useState } from "react";
import { MdOutlineMail, MdCheckCircle, MdArrowBack } from "react-icons/md";
import { Link } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";

import logoImg from "../../assets/logo.png";
import { api } from "../../services/api";

interface ForgotPasswordForm {
  email: string;
}

const schema = Yup.object().shape({
  email: Yup.string()
    .email("E-mail inválido")
    .required("Campo e-mail obrigatório"),
});

export function ResetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  async function handleSubmit({ email }: ForgotPasswordForm) {
    try {
      setIsLoading(true);

      await api.post("/forgot-password", { email });

      setEmailSent(true);
      enqueueSnackbar("E-mail de recuperação enviado com sucesso!", {
        variant: "success",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    } catch (error) {
      enqueueSnackbar(
        "Erro ao enviar e-mail. Verifique o endereço informado.",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        },
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0085BD] via-[#006a96] to-[#004d6d]">
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#92C163]/10 rounded-full blur-3xl" />
      </div>

      {/* Card */}
      <div className="relative w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              <img className="w-40 h-auto" src={logoImg} alt="Logo Luvilimp" />
            </div>
          </div>

          {emailSent ? (
            /* Estado de sucesso */
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-[#92C163]/20 rounded-full flex items-center justify-center">
                  <MdCheckCircle className="text-[#92C163]" size={48} />
                </div>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                E-mail Enviado!
              </h1>
              <p className="text-white/70 text-sm mb-8 leading-relaxed">
                Verifique sua caixa de entrada e clique no link para redefinir sua senha.
                O link expira em 1 hora.
              </p>
              <Link
                to="/signin"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <MdArrowBack size={18} />
                Voltar para o login
              </Link>
            </div>
          ) : (
            /* Formulário */
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Recuperar Senha
                </h1>
                <p className="text-white/70 text-sm">
                  Digite seu e-mail e enviaremos um link para redefinir sua senha
                </p>
              </div>

              <Formik
                initialValues={{ email: "" }}
                validationSchema={schema}
                onSubmit={values => {
                  handleSubmit(values);
                }}
              >
                {({ errors, touched }) => (
                  <Form className="space-y-5">
                    {/* Campo Email */}
                    <div>
                      <label className="block text-white/90 text-sm font-medium mb-2">
                        E-mail
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <MdOutlineMail className="text-white/50" size={20} />
                        </div>
                        <Field
                          type="email"
                          name="email"
                          placeholder="seu@email.com"
                          className={`w-full h-14 pl-12 pr-4 bg-white/10 border ${
                            errors.email && touched.email
                              ? "border-red-400"
                              : "border-white/20 focus:border-white/50"
                          } rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all`}
                        />
                      </div>
                      {errors.email && touched.email && (
                        <p className="mt-1 text-red-300 text-xs">{errors.email}</p>
                      )}
                    </div>

                    {/* Botão Submit */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-14 bg-[#92C163] hover:bg-[#7aab4e] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        "Enviar Link de Recuperação"
                      )}
                    </button>

                    {/* Link Voltar */}
                    <Link
                      to="/signin"
                      className="flex items-center justify-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
                    >
                      <MdArrowBack size={16} />
                      Voltar para o login
                    </Link>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-white/50 text-sm mt-6">
          © {new Date().getFullYear()} Luvilimp. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
